import { getMemberId, isMemberLoggedIn } from '@trello/authentication';
import { sendErrorEvent } from '@trello/error-reporting';
import { client } from '@trello/graphql';
import {
  convertToPIIString,
  dangerouslyConvertPrivacyString,
} from '@trello/privacy';
import { cacheFactory } from '@trello/quickload';
import {
  getMemberActivityUrl,
  getMemberProfileUrl,
  isMeAlias,
} from '@trello/urls';

import { memberPage } from './memberPage';
import type {
  MemberProfileQuery,
  MemberProfileQueryVariables,
} from './MemberProfileQuery.generated';
import { MemberProfileDocument } from './MemberProfileQuery.generated';

export const memberProfilePage = async function (
  username = convertToPIIString('me'),
  section?: string | null,
  subsection?: string | null,
) {
  if (isMemberLoggedIn()) {
    const memberId = getMemberId() ?? 'me';
    await cacheFactory.waitForQueryHydratedTo('MemberHeader', 'Apollo');
    const { data, error } = await client.query<
      MemberProfileQuery,
      MemberProfileQueryVariables
    >({
      query: MemberProfileDocument,
      variables: {
        id: memberId,
      },
      context: {
        operationName: 'MemberProfile',
        document: MemberProfileDocument,
      },
    });
    const myUsername = data?.member?.username;
    if (error || !myUsername) {
      sendErrorEvent(
        error ||
          new Error(`Unable to retrieve username for memberId ${memberId}`),
        {
          tags: {
            ownershipArea: 'trello-web-eng',
          },
        },
      );
      // we can always go to the activity tab even if not logged in, so we will default to that tab if there's an error
      // @ts-expect-error
      return memberPage.call(this, {
        username,
        loadFn: 'loadMemberProfileData',
        preloadFn: 'loadMemberProfileMinimal',
        viewClassName: 'MemberActivityView',
        // @ts-expect-error
        getLocation: getMemberActivityUrl,
        titleKey: 'activity',
      });
    }
    if (
      myUsername === username ||
      isMeAlias(dangerouslyConvertPrivacyString(username))
    ) {
      switch (section) {
        case 'activity':
          // @ts-expect-error
          return memberPage.call(this, {
            // use `myUsername` to account for the use of an alias
            username: myUsername,
            loadFn: 'loadMemberProfileData',
            preloadFn: 'loadMemberProfileMinimal',
            viewClassName: 'MemberActivityView',
            // @ts-expect-error
            getLocation: getMemberActivityUrl,
            titleKey: 'activity',
          });
        default:
          // @ts-expect-error
          return memberPage.call(this, {
            // use `myUsername` to account for the use of an alias
            username: myUsername,
            loadFn: 'loadMemberProfileData',
            preloadFn: 'loadMemberProfileMinimal',
            viewClassName: 'MemberProfileView',
            // @ts-expect-error
            getLocation: getMemberProfileUrl,
            titleKey: 'profile',
          });
      }
    } else {
      // If we are accessing the profile of another user, we can only see the cards or activity tabs
      switch (section) {
        default:
          // @ts-expect-error
          return memberPage.call(this, {
            username,
            loadFn: 'loadMemberProfileData',
            preloadFn: 'loadMemberProfileMinimal',
            viewClassName: 'MemberActivityView',
            // @ts-expect-error
            getLocation: getMemberActivityUrl,
            titleKey: 'activity',
          });
      }
    }
  } else {
    // If we aren't logged in, we can only see the cards or activity tabs
    switch (section) {
      default:
        // @ts-expect-error
        return memberPage.call(this, {
          username,
          loadFn: 'loadMemberProfileData',
          preloadFn: 'loadMemberProfileMinimal',
          viewClassName: 'MemberActivityView',
          // @ts-expect-error
          getLocation: getMemberActivityUrl,
          titleKey: 'activity',
        });
    }
  }
};
